import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle, X, Send, Bot, User, Loader2,
  RotateCcw, ChevronDown, Minimize2,
} from 'lucide-react';
import { useLang } from '@/hooks/useLang';

/* ── Tipuri ──────────────────────────────────────────────────────────────── */
interface Message {
  role: 'user' | 'assistant';
  content: string;
  ts: number;
}

/* ── Endpoint proxy PHP ──────────────────────────────────────────────────── */
// Apel direct OpenAI din browser (CDN static — PHP nu rulează)
// OpenAI Responses API (mai nou, suportă gpt-5.x)
const OPENAI_ENDPOINT = 'https://api.openai.com/v1/responses';

function getApiKey(): string {
  return localStorage.getItem('interpersonal_openai_key') || '';
}
function getModel(): string {
  return localStorage.getItem('interpersonal_chatbot_model') || 'gpt-5.4-mini';
}

const SYSTEM_PROMPT = `Esti asistentul virtual AI al companiei INTERPERSONAL S.R.L., furnizor complet de servicii si produse IT din Romania.

DESPRE COMPANIE:
- Denumire: INTERPERSONAL S.R.L. | CUI: RO34799221 | J2015002538235
- Adresa: Str. Orhideelor nr. 8, Sat Teghes, Com. Domnesti, Jud. Ilfov, 077091
- Telefon: +40 758 011 010, +40 741 407 595 | Email: office@interpersonal.ro
- Program: Luni-Vineri 09:00-18:00 | Suport tehnic 24/7
- Configurator preturi: https://websiteconfigurator.interpersonal.ro/

SERVICII SI PRETURI ORIENTATIVE:
1. WEB DESIGN: site prezentare 1.200-2.500 EUR; site business 3.500-6.000 EUR; eCommerce 5.000-15.000 EUR; SEO lunar 400-900 EUR
2. CALL CENTER: servicii inbound (InfoLine, TicketDesk, HelpDesk), outbound (Telemarketing, Lead Gen, Sondaje); tarife la cerere
3. IT HARDWARE: calculatoare, laptopuri, servere, networking Cisco/HP/Dell/Ubiquiti, imprimante, componente gaming; livrare rapida, garantie
4. GAZDUIRE WEB: START 5 EUR/luna, BUSINESS 25 EUR/luna, PRO 80 EUR/luna (SSD NVMe, SSL, backup zilnic); minim 12 luni
5. TELEFONIE VOIP: centrale IP, numere DDI, apeluri HD; solutii pentru 2-500 utilizatori; tarife la cerere
6. CRM & SOFTWARE: implementare CRM, aplicatii custom, automatizari; oferta personalizata

COMPORTAMENT OBLIGATORIU:
- Raspunde EXCLUSIV cu informatii despre Interpersonal si serviciile/produsele sale
- Raspunde in limba utilizatorului (romana sau engleza)
- Fii prietenos, profesionist, concis (max 120 cuvinte per raspuns)
- Pentru oferte exacte: trimite la office@interpersonal.ro sau la configuratorul de preturi
- Daca intrebarea nu e legata de Interpersonal sau IT, redirectioneaza elegant catre serviciile oferite
- NU inventa informatii; daca nu cunosti un detaliu specific, spune ca echipa va oferi detalii`;

/* ── Session ID unic per sesiune browser ────────────────────────────────── */
const SESSION_ID = (() => {
  let s = sessionStorage.getItem('ai_session_id');
  if (!s) { s = crypto.randomUUID(); sessionStorage.setItem('ai_session_id', s); }
  return s;
})();

/* ── Device fingerprint (trimis cu fiecare request) ─────────────────────── */
const DEVICE_INFO = {
  screenWidth:  window.screen.width,
  screenHeight: window.screen.height,
  timezone:     Intl.DateTimeFormat().resolvedOptions().timeZone,
  lang:         navigator.language,
  platform:     (navigator as any).userAgentData?.platform || navigator.platform || 'unknown',
};

/* ── Mesaje de bun venit ─────────────────────────────────────────────────── */
const WELCOME_RO = `Bună ziua! 👋 Sunt asistentul virtual **Interpersonal AI**.

Vă pot ajuta cu informații despre:
• 🖥️ Web Design & eCommerce
• 📞 Call Center & VoIP
• 🖥️ IT & Hardware
• ☁️ Găzduire Web
• 🔧 CRM & Software

Cu ce vă pot ajuta astăzi?`;

const WELCOME_EN = `Hello! 👋 I'm **Interpersonal AI**, your virtual assistant.

I can help you with:
• 🖥️ Web Design & eCommerce
• 📞 Call Center & VoIP
• 💻 IT & Hardware
• ☁️ Web Hosting
• 🔧 CRM & Software

How can I help you today?`;

/* ── Quick questions ─────────────────────────────────────────────────────── */
const QUICK_RO = ['Ce servicii oferiți?', 'Prețuri hosting?', 'Cum vă contactez?', 'Despre Web Design'];
const QUICK_EN = ['What services do you offer?', 'Hosting prices?', 'How to contact you?', 'About Web Design'];

/* ── Markdown simplu (bold + newlines) ──────────────────────────────────── */
function renderMarkdown(text: string) {
  const html = text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .split('\n')
    .map(line => `<span>${line}</span>`)
    .join('<br />');
  return { __html: html };
}

/* ── Componenta ChatBot ──────────────────────────────────────────────────── */
export default function ChatBot() {
  const { lang, t } = useLang();
  const [open, setOpen]         = useState(false);
  const [minimized, setMin]     = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [unread, setUnread]     = useState(0);
  const [showQuick, setShowQuick] = useState(true);

  const bottomRef  = useRef<HTMLDivElement>(null);
  const inputRef   = useRef<HTMLTextAreaElement>(null);

  const welcome = lang === 'ro' ? WELCOME_RO : WELCOME_EN;
  const quick   = lang === 'ro' ? QUICK_RO : QUICK_EN;

  /* Inițializare mesaj bun-venit */
  useEffect(() => {
    setMessages([{ role: 'assistant', content: welcome, ts: Date.now() }]);
  }, [lang]);

  /* Scroll to bottom */
  const scrollBottom = useCallback(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
  }, []);

  useEffect(() => { if (open && !minimized) scrollBottom(); }, [messages, open, minimized]);

  /* Unread badge */
  useEffect(() => {
    if (!open) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg?.role === 'assistant' && messages.length > 1) setUnread(u => u + 1);
    }
  }, [messages]);

  const openChat = () => { setOpen(true); setMin(false); setUnread(0); };

  /* Trimitere mesaj ──────────────────────────────────────────────────────── */
  const send = useCallback(async (text?: string) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;

    setInput('');
    setError('');
    setShowQuick(false);

    const userMsg: Message = { role: 'user', content: msg, ts: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const apiKey = getApiKey();
      if (!apiKey) {
        throw new Error('Cheia API nu este configurată. Contactați administratorul.');
      }

      // Construim inputul ca array de mesaje pentru Responses API
      const history = messages.slice(-12).concat(userMsg).map(m => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }));

      const res = await fetch(OPENAI_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: getModel(),
          instructions: SYSTEM_PROMPT,
          input: history,
          store: true,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const errMsg = data?.error?.message || `Eroare API (${res.status})`;
        if (errMsg.includes('invalid_api_key') || errMsg.includes('Incorrect API key')) {
          throw new Error('Cheia OpenAI este invalidă. Contactați administratorul.');
        }
        if (errMsg.includes('quota') || errMsg.includes('billing')) {
          throw new Error('Limita OpenAI atinsă. Adăugați credit pe platform.openai.com');
        }
        throw new Error(errMsg);
      }

      // Responses API: data.output[0].content[0].text
      const reply =
        data?.output?.[0]?.content?.[0]?.text ||
        data?.output_text ||
        null;
      if (!reply) throw new Error('Răspuns invalid de la AI.');

      setMessages(prev => [...prev, { role: 'assistant', content: reply, ts: Date.now() }]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Eroare necunoscută';
      setError(msg);
      console.error('ChatBot error:', msg);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, loading, messages, t]);

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const reset = () => {
    setMessages([{ role: 'assistant', content: welcome, ts: Date.now() }]);
    setShowQuick(true); setError(''); setInput('');
  };

  return (
    <>
      {/* ── Fereastra chat ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.88, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.88, y: 20 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="fixed z-[55] bottom-[5.5rem] right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[420px] flex flex-col rounded-2xl overflow-hidden shadow-2xl"
            style={{
              height: minimized ? 'auto' : 'clamp(420px, 65vh, 580px)',
              background: '#0d1018',
              border: '1px solid rgba(74,184,255,0.2)',
              boxShadow: '0 0 60px rgba(74,184,255,0.12), 0 20px 60px rgba(0,0,0,0.6)',
            }}>

            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#0d1a35,#0a1628)', borderBottom: '1px solid rgba(74,184,255,0.12)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg,#295CA8,#4ab8ff)', boxShadow: '0 0 14px rgba(74,184,255,0.4)' }}>
                <Bot size={15} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white leading-none">Interpersonal AI</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-[10px] text-white/40">{t('Online · Răspund instant','Online · Instant replies')}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={reset} title={t('Conversație nouă','New conversation')}
                  className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/5 transition-all">
                  <RotateCcw size={13} />
                </button>
                <button onClick={() => setMin(v => !v)} title={t('Minimizează','Minimize')}
                  className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/5 transition-all">
                  {minimized ? <ChevronDown size={14} className="rotate-180" /> : <Minimize2 size={13} />}
                </button>
                <button onClick={() => setOpen(false)} title={t('Închide','Close')}
                  className="p-1.5 rounded-lg text-white/30 hover:text-red-400/80 hover:bg-red-400/8 transition-all">
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Body — ascuns când minimized */}
            {!minimized && (
              <>
                {/* Mesaje */}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-thin"
                  style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(74,184,255,0.15) transparent' }}>

                  {messages.map((msg, i) => (
                    <motion.div key={i}
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.22 }}
                      className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>

                      {/* Avatar */}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        msg.role === 'assistant'
                          ? 'bg-gradient-to-br from-[#295CA8] to-[#4ab8ff]'
                          : 'bg-white/10 border border-white/15'
                      }`}>
                        {msg.role === 'assistant'
                          ? <Bot size={12} className="text-white" />
                          : <User size={11} className="text-white/70" />}
                      </div>

                      {/* Bubble */}
                      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                        msg.role === 'user'
                          ? 'text-white rounded-tr-sm'
                          : 'text-white/85 rounded-tl-sm'
                      }`} style={msg.role === 'user'
                        ? { background: 'linear-gradient(135deg,#295CA8,#3D72B8)', boxShadow: '0 2px 12px rgba(41,92,168,0.35)' }
                        : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        <span dangerouslySetInnerHTML={renderMarkdown(msg.content)} />
                      </div>
                    </motion.div>
                  ))}

                  {/* Quick suggestions (prima deschidere) */}
                  {showQuick && messages.length <= 1 && (
                    <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.3 }}
                      className="flex flex-wrap gap-2 pt-1">
                      {quick.map((q,i) => (
                        <button key={i} onClick={() => send(q)}
                          className="text-xs px-3 py-1.5 rounded-full border border-[#295CA8]/40 text-[#4ab8ff]/80 hover:bg-[#295CA8]/20 hover:text-[#4ab8ff] transition-all">
                          {q}
                        </button>
                      ))}
                    </motion.div>
                  )}

                  {/* Loading indicator */}
                  {loading && (
                    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} className="flex gap-2.5">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#295CA8] to-[#4ab8ff] flex items-center justify-center flex-shrink-0">
                        <Bot size={12} className="text-white" />
                      </div>
                      <div className="rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1.5"
                        style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.07)' }}>
                        {[0,1,2].map(d => (
                          <motion.span key={d} className="w-1.5 h-1.5 rounded-full bg-[#4ab8ff]/60"
                            animate={{ y:[0,-4,0] }} transition={{ repeat:Infinity, duration:0.9, delay:d*0.18 }} />
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* Error */}
                  {error && (
                    <div className="flex items-start gap-2 rounded-xl px-3 py-2.5 text-xs text-red-300/80"
                      style={{ background:'rgba(255,60,60,0.07)', border:'1px solid rgba(255,60,60,0.15)' }}>
                      ⚠️ {error}
                    </div>
                  )}

                  <div ref={bottomRef} />
                </div>

                {/* Input */}
                <div className="flex-shrink-0 px-3 pb-3 pt-2"
                  style={{ borderTop:'1px solid rgba(255,255,255,0.06)' }}>
                  <div className="flex items-end gap-2 rounded-xl px-3 py-2"
                    style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.09)' }}>
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={handleKey}
                      placeholder={t('Scrieți un mesaj... (Enter = trimite)','Type a message... (Enter = send)')}
                      rows={1}
                      disabled={loading}
                      className="flex-1 bg-transparent text-sm text-white placeholder-white/25 outline-none resize-none max-h-28 leading-relaxed disabled:opacity-50"
                      style={{ scrollbarWidth:'none' }}
                    />
                    <button
                      onClick={() => send()}
                      disabled={!input.trim() || loading}
                      className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all disabled:opacity-30"
                      style={{ background: input.trim() && !loading ? 'linear-gradient(135deg,#295CA8,#4ab8ff)' : 'rgba(255,255,255,0.06)' }}>
                      {loading ? <Loader2 size={14} className="text-white animate-spin" /> : <Send size={13} className="text-white" />}
                    </button>
                  </div>
                  <p className="text-[9px] text-white/18 text-center mt-1.5">
                    {t('Powered by OpenAI · Interpersonal S.R.L.','Powered by OpenAI · Interpersonal S.R.L.')}
                  </p>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Buton flotant ───────────────────────────────────────────────── */}
      <motion.button
        onClick={open ? () => setOpen(false) : openChat}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 right-4 sm:right-6 z-[56] w-13 h-13 rounded-full flex items-center justify-center shadow-2xl"
        style={{
          width: '52px', height: '52px',
          background: open ? 'rgba(41,92,168,0.85)' : 'linear-gradient(135deg,#295CA8 0%,#4ab8ff 100%)',
          boxShadow: open ? '0 4px 20px rgba(41,92,168,0.5)' : '0 0 0 0 rgba(74,184,255,0.5), 0 6px 30px rgba(74,184,255,0.4)',
        }}
        aria-label="Chat AI">
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate:-90, opacity:0 }} animate={{ rotate:0, opacity:1 }} exit={{ rotate:90, opacity:0 }} transition={{ duration:0.18 }}>
              <X size={20} className="text-white" />
            </motion.span>
          ) : (
            <motion.span key="chat" initial={{ rotate:90, opacity:0 }} animate={{ rotate:0, opacity:1 }} exit={{ rotate:-90, opacity:0 }} transition={{ duration:0.18 }}>
              <MessageCircle size={20} className="text-white" />
            </motion.span>
          )}
        </AnimatePresence>

        {/* Badge mesaje necitite */}
        <AnimatePresence>
          {!open && unread > 0 && (
            <motion.span key="badge"
              initial={{ scale:0 }} animate={{ scale:1 }} exit={{ scale:0 }}
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#0a0c12]">
              {unread > 9 ? '9+' : unread}
            </motion.span>
          )}
        </AnimatePresence>

        {/* Puls animat (când închis) */}
        {!open && (
          <span className="absolute inset-0 rounded-full animate-ping opacity-20"
            style={{ background:'linear-gradient(135deg,#295CA8,#4ab8ff)', animationDuration:'2.5s' }} />
        )}
      </motion.button>
    </>
  );
}
